import React, { useState, useMemo, useRef, useEffect } from "react";
import { List as VirtualizedList, AutoSizer } from "react-virtualized";
import FilterCheckbox from "../FilterCheckbox";

export const defaultClosedFilters = {
    kategoria: true,
    nyelv: true,
};

const maxFilterHeights = {
    'kategoria#filter_2': 380,
}

export default function FilterBox(props) {
    let {
        id,
        title,
        count,
        start_date: startDate,
        end_date: endDate,
        param,
        items,
        selected,
        selectable,
        noCheckMark,
        multi,
        onSelect,
        searchable,
        searchInput,
        setSearchInput,
        openFilters,
        toggleFilterOpen,
        className = "",
        parent = true,
    } = props;
    const filterOpen = openFilters[param + "#" + id] ?? !defaultClosedFilters[param];
    const isSelected = selectable && !!selected[param]?.find(sel => sel.id == id || sel.old_id == id);
    const listRef = useRef();

    // Memoize filtered items for performance
    const filteredItems = useMemo(() => {
        if (!items) return [];
        return items.filter(item => {
            const isSelected = !!selected[param]?.find(sel => sel.id === item.id);
            if (searchable) {
                return (
                    item.title?.toLowerCase().includes(searchInput?.toLowerCase()) ||
                    item.items?.some(i => i.title?.toLowerCase().includes(searchInput?.toLowerCase())) ||
                    isSelected
                );
            }
            return true;
        });
    }, [items, selected, searchInput, searchable]);

    const heights = useMemo(() => {
        if(filteredItems.length === 0) return [0];

        return filteredItems.map((item, index) => getRowHeight({ ...props, index, filteredItems, openFilters, toggleFilterOpen }));
    }, [filteredItems, openFilters]);

    const selectedIndex = useMemo(() => {
        return filteredItems.findIndex(item =>
            Object.values(selected).flat().some(sel => sel.id == item.id || sel.old_id == item.id)
        );
    }, [filteredItems, selected, selectable]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.recomputeRowHeights();
            listRef.current.forceUpdate();
        }
    }, [heights]);

    return (
        <div className={`widget-lister-filter-box ${className}${filterOpen ? " open" : " closed"}${(selectable && parent) ? " selectable" : ""}`}>
            <div className="list-filter-group">
                <div
                    className={"list-filter-title" + (selectable ? " filter-checkbox-container" : "")}
                    onClick={!noCheckMark ? handleTitleClick : null}
                    style={searchable && filterOpen ? { marginBottom: 0 } : {}}
                >
                    {title}
                    {startDate && endDate && <span className="list-filter-title__date"> {startDate}-{endDate}</span>}
                    {count && <span className="list-filter-title__count">({count})</span>}
                    {multi && <span className="list-filter-title-multi">(több választható)</span>}
                    {(selectable && !noCheckMark) && (<>
                        <input type="checkbox" checked={isSelected} onChange={() => {}} />
                        <div className="checkmark" />
                    </>)}
                </div>
                {searchable && filterOpen && (
                    <div className="input bg-transparent mb-2" style={{ flex: 1 }}>
                        <input
                            type="text"
                            className="form-control bg-white"
                            placeholder="Keresés"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            style={{ fontSize: 17, borderRadius: 8 }}
                        />
                        {searchInput.length > 1 && (
                            <div className="reset" onClick={() => setSearchInput("")}></div>
                        )}
                        <input type="hidden" name="q" value={searchInput} />
                    </div>
                )}

                <div style={{ height: filterOpen ? Math.min(maxFilterHeights[param+'#'+id] ?? maxFilterHeights[param] ?? 150, heights.reduce((a, b) => a + b)) : 0, transition: parent ? "height 0.3s" : "none", overflow: "hidden" }}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <VirtualizedList
                                ref={listRef}
                                width={width}
                                height={height}
                                rowCount={filteredItems.length}
                                rowHeight={rowProps => heights[rowProps.index]}
                                rowRenderer={rowProps => rowRenderer({ ...rowProps, ...props, filteredItems, openFilters, toggleFilterOpen })}
                                overscanRowCount={5}
                                style={{ outline: "none" }}
                                scrollToIndex={selectedIndex >= 0 ? selectedIndex : undefined}
                                scrollToAlignment="center"
                            />
                        )}
                    </AutoSizer>
                </div>
            </div>
        </div>
    );

    function handleTitleClick(elem) {
        if(selectable) {
            if(elem.target.tagName.toLowerCase() === "input" || elem.target.classList.contains("checkmark") || !items) {
                onSelect({ id: id, param, title: title, multi });

                if(!items || filterOpen)
                    return;
            }
        }

        toggleFilterOpen(param + "#" + id, !filterOpen, defaultClosedFilters[param] || false);
    }
}

// Row renderer for react-virtualized
const rowRenderer = ({ index, key, style, filteredItems, selected, multi, onSelect, param, openFilters, toggleFilterOpen, searchInput }) => {
    const item = filteredItems[index];
    const isSelected = !!selected[item.param]?.find(sel => sel.id == item.id || sel.old_id == item.id);    

    if (item.items?.length) {
        return (
            <div key={key} style={style}>
                <FilterBox 
                    id={item.id}
                    title={item.short_title ?? item.title}
                    param={item.param || param}
                    items={item.items?.filter(i => i.title?.toLowerCase().includes(searchInput?.toLowerCase()) || !!selected[param]?.find(sel => sel.id === i.id))}
                    selected={selected}
                    multi={false}
                    onSelect={onSelect}
                    selectable={!!item.selectable}
                    openFilters={openFilters}
                    toggleFilterOpen={toggleFilterOpen}
                    parent={false}
                />
            </div>
        );
    }

    return (
        <div key={key} style={style}>
            <FilterCheckbox
                multi={multi}
                param={item.param}
                item={item}
                selected={isSelected}
                onSelect={onSelect}
            />
        </div>
    );
};

const getRowHeight = ({ index, filteredItems, openFilters, searchInput, selected, param }) => {
    const item = filteredItems[index];
    const items = item.items?.filter(i => i.title?.toLowerCase().includes(searchInput?.toLowerCase()) || !!selected[param]?.find(sel => sel.id === i.id));
    
    if(!items)
        return 25; // Default height for single items
    
    const length = openFilters[item.param + "#" + item.id] ?? !defaultClosedFilters[item.param] ? items.length || 0 : 0;
    
    return Math.min(maxFilterHeights[item.param + "#" + item.id] ?? maxFilterHeights[item.param] ?? 150, (length * 25)) + 10 + 25; // Adjust height if there are sub-items
}